import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, statusBadgeClass, statusLabel } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  BookOpen,
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  X,
  Edit2,
  Trash2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  RefreshCw,
} from 'lucide-react';

function AccountRow({ account, onEdit, onDelete }) {
  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{account.code || account.account_code || '-'}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          {account.type === 'asset' && <TrendingUp className="h-4 w-4 text-primary-800" />}
          {account.type === 'liability' && <TrendingDown className="h-4 w-4 text-danger-600" />}
          {account.type === 'equity' && <DollarSign className="h-4 w-4 text-success-600" />}
          {account.type === 'revenue' && <TrendingUp className="h-4 w-4 text-primary-500" />}
          {account.type === 'expense' && <TrendingDown className="h-4 w-4 text-warning-600" />}
          <span className="text-sm text-gray-700 dark:text-gray-200">{account.name || account.account_name}</span>
        </div>
      </td>
      <td>
        <span className={`badge ${getAccountTypeBadge(account.type)}`}>{getAccountTypeLabel(account.type)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{account.group_name || account.group || account.grouping || '-'}</span>
      </td>
      <td>
        <span className={`text-sm font-medium ${account.normal_balance === 'debit' ? 'text-primary-900 dark:text-primary-400' : 'text-danger-700 dark:text-danger-400'}`}>{account.normal_balance === 'debit' ? 'Debet' : 'Kredit'}</span>
      </td>
      <td className="text-right">
        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(account.balance)}</span>
      </td>
      <td>
        <span className={`badge ${statusBadgeClass(account.is_active ? 'active' : 'inactive')}`}>{statusLabel(account.is_active ? 'active' : 'inactive')}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onEdit(account)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(account)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function getAccountTypeBadge(type) {
  const map = {
    asset: 'badge-info', liability: 'badge-danger', equity: 'badge-success',
    revenue: 'badge-purple', expense: 'badge-warning',
  };
  return map[type] || 'badge-gray';
}

function getAccountTypeLabel(type) {
  const map = {
    asset: 'Aset', liability: 'Kewajiban', equity: 'Ekuitas',
    revenue: 'Pendapatan', expense: 'Beban',
  };
  return map[type] || type;
}

function AccountCard({ account }) {
  return (
    <div className="stat-card">
      <div className={`stat-icon ${getAccountTypeBadge(account.type).replace('badge', 'bg')}`}>
        {account.type === 'asset' && <TrendingUp className="h-6 w-6" />}
        {account.type === 'liability' && <TrendingDown className="h-6 w-6" />}
        {account.type === 'equity' && <DollarSign className="h-6 w-6" />}
        {account.type === 'revenue' && <TrendingUp className="h-6 w-6" />}
        {account.type === 'expense' && <TrendingDown className="h-6 w-6" />}
      </div>
      <div className="flex-1">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{account.code || account.account_code}</p>
        <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">{account.name || account.account_name}</p>
        <p className="text-xs text-gray-400 mt-1">{account.group_name || account.group}</p>
      </div>
    </div>
  );
}

export default function AccountsPage() {
  const { hasPermission } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', type: '', group: '', status: '' });
  const [groupedAccounts, setGroupedAccounts] = useState({});

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', type: 'asset', group: '', normal_balance: 'debit',
    description: '', is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, [pagination.page]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/accounts', { params });
      if (res.data.success) {
        const data = res.data.data;
        const accountsList = data?.accounts || data?.data || [];
        setAccounts(accountsList);
        // Group by type for better display
        const grouped = {};
        accountsList.forEach((acc) => {
          if (!grouped[acc.type]) grouped[acc.type] = [];
          grouped[acc.type].push(acc);
        });
        setGroupedAccounts(grouped);
        setPagination((prev) => ({
          ...prev,
          total: data?.total || data?.pagination?.total || 0,
          totalPages: data?.totalPages || data?.pagination?.totalPages || Math.ceil((data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/accounts', form);
      if (res.data.success) {
        toast.success('Akun berhasil dibuat');
        setShowCreateModal(false);
        resetForm();
        fetchAccounts();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/accounts/${selectedAccount.id}`, form);
      if (res.data.success) {
        toast.success('Akun berhasil diperbarui');
        setShowEditModal(false);
        setSelectedAccount(null);
        resetForm();
        fetchAccounts();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (account) => {
    if (!confirm(`Yakin hapus akun ${account.name}?`)) return;
    try {
      const res = await api.delete(`/accounts/${account.id}`);
      if (res.data.success) {
        toast.success('Akun berhasil dihapus');
        fetchAccounts();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setForm({
      code: account.code || account.account_code || '',
      name: account.name || account.account_name || '',
      type: account.type || 'asset',
      group: account.group_name || account.group || '',
      normal_balance: account.normal_balance || 'debit',
      description: account.description || '',
      is_active: account.is_active !== false,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setForm({
      code: '', name: '', type: 'asset', group: '', normal_balance: 'debit',
      description: '', is_active: true,
    });
  };

  const accountTypes = [
    { value: 'asset', label: 'Aset', color: 'bg-primary-600' },
    { value: 'liability', label: 'Kewajiban', color: 'bg-danger-600' },
    { value: 'equity', label: 'Ekuitas', color: 'bg-success-600' },
    { value: 'revenue', label: 'Pendapatan', color: 'bg-primary-700' },
    { value: 'expense', label: 'Beban', color: 'bg-warning-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Daftar Akun (Chart of Accounts)</h1>
          <p className="page-subtitle">Kelola struktur akun akuntansi</p>
        </div>
        {hasPermission('create_accounts') && (
          <button onClick={() => { resetForm(); setShowCreateModal(true); }} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Tambah Akun
          </button>
        )}
      </div>

      {/* Account Cards Summary */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {accountTypes.map((type) => (
            <AccountCard key={type.value} account={{ ...type, balance: groupedAccounts[type.value]?.reduce((sum, a) => sum + (a.balance || 0), 0) || 0, count: groupedAccounts[type.value]?.length || 0 }} />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari kode, nama..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && (setPagination((prev) => ({ ...prev, page: 1 })), fetchAccounts())}
              />
            </div>
            <div>
              <select className="input" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
                <option value="">Semua Tipe</option>
                <option value="asset">Aset</option>
                <option value="liability">Kewajiban</option>
                <option value="equity">Ekuitas</option>
                <option value="revenue">Pendapatan</option>
                <option value="expense">Beban</option>
              </select>
            </div>
            <div>
              <select className="input" value={filters.group} onChange={(e) => setFilters({ ...filters, group: e.target.value })}>
                <option value="">Semua Grup</option>
                <option value="Aktiva">Aktiva</option>
                <option value="Pasiva">Pasiva</option>
                <option value="Ekuitas">Ekuitas</option>
                <option value="Pendapatan">Pendapatan</option>
                <option value="Beban">Beban</option>
              </select>
            </div>
            <div>
              <select className="input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Semua Status</option>
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setPagination((prev) => ({ ...prev, page: 1 })); fetchAccounts(); }} className="btn-primary btn-sm flex-1">
                <Search className="h-4 w-4 mr-1" /> Cari
              </button>
              <button onClick={() => { setFilters({ search: '', type: '', group: '', status: '' }); setPagination((prev) => ({ ...prev, page: 1 })); fetchAccounts(); }} className="btn-secondary btn-sm">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-40" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-32 ml-auto" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-24" />
                </div>
              ))}
            </div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Akun</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada daftar akun yang terdaftar.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kode</th>
                    <th>Nama</th>
                    <th>Tipe</th>
                    <th>Grup</th>
                    <th>Saldo Normal</th>
                    <th className="text-right">Saldo</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((acc) => (
                    <AccountRow key={acc.id} account={acc} onEdit={handleEdit} onDelete={handleDelete} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page <= 1}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                    Halaman {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Tambah Akun Baru</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="label">Kode Akun <span className="text-danger-600">*</span></label>
                <input type="text" className="input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required placeholder="Contoh: 1110" />
              </div>
              <div>
                <label className="label">Nama Akun <span className="text-danger-600">*</span></label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Nama akun" />
              </div>
              <div>
                <label className="label">Tipe Akun <span className="text-danger-600">*</span></label>
                <select className="input" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} required>
                  <option value="asset">Aset</option>
                  <option value="liability">Kewajiban</option>
                  <option value="equity">Ekuitas</option>
                  <option value="revenue">Pendapatan</option>
                  <option value="expense">Beban</option>
                </select>
              </div>
              <div>
                <label className="label">Grup</label>
                <select className="input" value={form.group} onChange={(e) => setForm({...form, group: e.target.value})}>
                  <option value="">Pilih Grup</option>
                  <option value="Aktiva">Aktiva</option>
                  <option value="Pasiva">Pasiva</option>
                  <option value="Ekuitas">Ekuitas</option>
                  <option value="Pendapatan">Pendapatan</option>
                  <option value="Beban">Beban</option>
                </select>
              </div>
              <div>
                <label className="label">Saldo Normal <span className="text-danger-600">*</span></label>
                <select className="input" value={form.normal_balance} onChange={(e) => setForm({...form, normal_balance: e.target.value})} required>
                  <option value="debit">Debet</option>
                  <option value="credit">Kredit</option>
                </select>
              </div>
              <div>
                <label className="label">Deskripsi</label>
                <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Deskripsi akun" rows={3} />
              </div>
              <div>
                <label className="label">Status</label>
                <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                  <option value="true">Aktif</option>
                  <option value="false">Nonaktif</option>
                </select>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedAccount && (
        <div className="modal-overlay" onClick={() => { setShowEditModal(false); setSelectedAccount(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Edit Akun</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedAccount(null); }} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="label">Kode Akun <span className="text-danger-600">*</span></label>
                <input type="text" className="input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required placeholder="Contoh: 1110" />
              </div>
              <div>
                <label className="label">Nama Akun <span className="text-danger-600">*</span></label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Nama akun" />
              </div>
              <div>
                <label className="label">Tipe Akun <span className="text-danger-600">*</span></label>
                <select className="input" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} required>
                  <option value="asset">Aset</option>
                  <option value="liability">Kewajiban</option>
                  <option value="equity">Ekuitas</option>
                  <option value="revenue">Pendapatan</option>
                  <option value="expense">Beban</option>
                </select>
              </div>
              <div>
                <label className="label">Grup</label>
                <select className="input" value={form.group} onChange={(e) => setForm({...form, group: e.target.value})}>
                  <option value="">Pilih Grup</option>
                  <option value="Aktiva">Aktiva</option>
                  <option value="Pasiva">Pasiva</option>
                  <option value="Ekuitas">Ekuitas</option>
                  <option value="Pendapatan">Pendapatan</option>
                  <option value="Beban">Beban</option>
                </select>
              </div>
              <div>
                <label className="label">Saldo Normal <span className="text-danger-600">*</span></label>
                <select className="input" value={form.normal_balance} onChange={(e) => setForm({...form, normal_balance: e.target.value})} required>
                  <option value="debit">Debet</option>
                  <option value="credit">Kredit</option>
                </select>
              </div>
              <div>
                <label className="label">Deskripsi</label>
                <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Deskripsi akun" rows={3} />
              </div>
              <div>
                <label className="label">Status</label>
                <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                  <option value="true">Aktif</option>
                  <option value="false">Nonaktif</option>
                </select>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedAccount(null); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Memperbarui...' : 'Perbarui'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
